<template>
  <div class="container-fluid px-4 mt-4">
    <!-- Fullscreen loading -->
    <div v-if="loading" class="loading-overlay">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p>Yükleniyor...</p>
    </div>

    <h4 class="mb-4 font-weight-bold text-dark text-center">
      İşletme Sipariş Raporu
    </h4>

    <!-- Filtre Formu -->
    <b-row class="justify-content-center">
      <b-col cols="12" md="10" lg="8">
        <b-form @submit.prevent="downloadReport" class="p-3 bg-white border rounded">
          <!-- Dönemler -->
          <b-row>
            <b-col cols="12">
              <p class="font-weight-bold">Tarih Aralıkları (En az 1, en fazla 3 dönem)</p>
            </b-col>

            <b-col
                v-for="(period, index) in reportForm.periods"
                :key="index"
                cols="12"
                class="mb-2"
            >
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group :label="`Dönem ${index + 1} Başlangıç`">
                    <b-form-datepicker
                        :id="`period-${index}-from`"
                        v-model="period.from"
                        placeholder="Başlangıç tarihi seçiniz"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="6">
                  <b-form-group :label="`Dönem ${index + 1} Bitiş`">
                    <b-form-datepicker
                        :id="`period-${index}-to`"
                        v-model="period.to"
                        placeholder="Bitiş tarihi seçiniz"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="12">
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="İşletmeler">
                    <multiselect
                        v-model="reportForm.clientIds"
                        :options="clients"
                        :multiple="true"
                        :close-on-select="false"
                        :clear-on-select="true"
                        :preserve-search="true"
                        track-by="id"
                        label="name"
                        placeholder="İşletme seçin"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="6">
                  <b-form-group label="Ürünler">
                    <multiselect
                        v-model="reportForm.productIds"
                        :options="products"
                        :multiple="true"
                        :close-on-select="false"
                        :clear-on-select="true"
                        :preserve-search="true"
                        track-by="id"
                        label="name"
                        placeholder="Ürün seçin"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <!-- Tek buton: Excel İndir -->
          <div class="d-flex justify-content-end mt-3">
            <b-button
                variant="success"
                type="submit"
                :disabled="isDownloadDisabled"
            >
              Excel İndir
            </b-button>
          </div>

          <div class="text-muted small mt-2">
            En az bir dönemin başlangıç ve bitiş tarihini doldurmanız gerekir.
          </div>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import ReportService from "@/service/ReportService";
import * as XLSX from "xlsx-js-style";
import Multiselect from "vue-multiselect";
import ClientService from "@/service/ClientService";
import ProductService from "@/service/ProductService";

export default {
  name: "shop-orders-report",
  components: { Multiselect },

  data() {
    return {
      loading: false,
      reportForm: {
        periods: [
          { from: null, to: null },
          { from: null, to: null },
          { from: null, to: null },
        ],
        clientIds: [],
        productIds: [],
      },
      report: null,
      clients: [],
      products: [],
    };
  },

  computed: {
    validPeriods() {
      return this.reportForm.periods.filter((p) => p.from && p.to);
    },

    isDownloadDisabled() {
      if (this.loading) return true;
      return this.validPeriods.length === 0;
    },
  },

  methods: {
    async downloadReport() {
      if (this.validPeriods.length === 0) {
        this.$bvToast?.toast("En az bir tarih aralığı doldurmanız gerekiyor.", {
          variant: "warning",
        });
        return;
      }

      if (this.validPeriods.length > 3) {
        this.$bvToast?.toast("En fazla 3 tarih aralığı seçebilirsiniz.", {
          variant: "warning",
        });
        return;
      }

      this.loading = true;

      try {
        const payload = {
          periods: this.validPeriods.map((p) => ({ from: p.from, to: p.to })),
          clientIds: this.reportForm.clientIds?.length
              ? this.reportForm.clientIds.map((c) => c.id)
              : null,
          productIds: this.reportForm.productIds?.length
              ? this.reportForm.productIds.map((p) => p.id)
              : null,
        };

        const res = await ReportService.getShopOrdersReport(payload);
        const report = res?.data ?? res;

        if (!report || !Array.isArray(report.rows) || report.rows.length === 0) {
          this.$bvToast?.toast("Kayıt bulunamadı.", { variant: "warning" });
          return;
        }

        this.report = report;
        this.exportToExcelPivotProductRows(report); // <-- ÜRÜN SATIR / İŞLETME KOLON
      } catch (e) {
        this.$bvToast?.toast("Rapor çekilirken bir hata oluştu.", { variant: "danger" });
      } finally {
        this.loading = false;
      }
    },

    // "2025-10-01" => "01.10.2025"
    formatDate(iso) {
      if (!iso) return "";
      const parts = iso.split("-");
      if (parts.length !== 3) return iso;
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    },

    formatPeriodLabel(period) {
      const from = this.formatDate(period.from);
      const to = this.formatDate(period.to);
      return `${from} - ${to}`;
    },

    /**
     * YENİ FORMAT:
     * - Satırlar: ÜRÜNLER
     * - Kolon blokları: İŞLETMELER (merge)
     * - Alt başlıklar: Dönem1 qty, (D2 qty + değişim), (D3 qty + değişim)
     */
    exportToExcelPivotProductRows(report) {
      const rows = report.rows || []; // backend: her satır client
      const productHeaders = report.productHeaders || []; // tüm ürünler
      const periods = report.periods || []; // [{from,to,label,index}, ...]

      if (!rows.length || !productHeaders.length || !periods.length) {
        this.$bvToast?.toast("Excel oluşturmak için yeterli veri yok.", { variant: "warning" });
        return;
      }

      // İşletme listesi: backend rows içinden (sipariş olmasa da backend artık döndürüyor)
      const clientList = rows.map((r) => ({ id: r.clientId, name: r.clientName }));
      const periodCount = periods.length;
      const columnsPerClient = periodCount + (periodCount - 1); // 3 dönem => 5 kolon
      const totalCols = 1 + clientList.length * columnsPerClient; // 1 = Ürün

      // ---------- Header satırları ----------
      const headerRow1 = new Array(totalCols).fill("");
      const headerRow2 = new Array(totalCols).fill("");

      headerRow1[0] = "Ürün";
      headerRow2[0] = "Ürün";

      const merges = [];
      const changeColIndexes = []; // % değişim kolon indexleri (renk + format)

      let currentCol = 1;

      // ÜST BAŞLIK: client isimleri merge
      clientList.forEach((client) => {
        const clientStartCol = currentCol;

        periods.forEach((p, idx) => {
          const label = this.formatPeriodLabel(p);

          // Adet kolonu
          headerRow2[currentCol] = label;
          currentCol++;

          // Değişim kolonu (2. dönemden itibaren)
          if (idx > 0) {
            headerRow2[currentCol] = "Bir önceki aya göre değişim";
            changeColIndexes.push(currentCol);
            currentCol++;
          }
        });

        const clientEndCol = currentCol - 1;
        headerRow1[clientStartCol] = client.name;

        merges.push({
          s: { r: 0, c: clientStartCol },
          e: { r: 0, c: clientEndCol },
        });
      });

      // ---------- Data satırları: her satır = ürün ----------
      // Backend rows: [{clientId, clientName, productPeriods:{productName:[{quantity,changePercent}...]}}]
      // Biz ürün bazlı satır üretip, her işletme bloğuna o ürünün dönem verilerini basacağız.
      const dataRows = [];

      // clientId -> row map hızlı erişim
      const clientIdToRow = new Map(rows.map((r) => [r.clientId, r]));

      productHeaders.forEach((productName) => {
        const dataRow = [];
        dataRow.push(productName);

        clientList.forEach((client) => {
          const clientRow = clientIdToRow.get(client.id);
          const productPeriods = clientRow?.productPeriods || {};
          const periodList = productPeriods[productName] || [];

          for (let p = 0; p < periodCount; p++) {
            const pa = periodList[p] || {};

            // quantity (int)
            let qty = pa.quantity != null ? pa.quantity : 0;
            if (typeof qty === "string") {
              const num = Number(qty.replace(",", "."));
              qty = isNaN(num) ? qty : num;
            }
            dataRow.push(qty);

            // change (2. dönemden itibaren)
            if (p > 0) {
              let change = pa.changePercent != null ? pa.changePercent : null;
              if (typeof change === "string") {
                const num = Number(change.replace(",", "."));
                change = isNaN(num) ? change : num;
              }
              dataRow.push(change);
            }
          }
        });

        dataRows.push(dataRow);
      });

      // ---------- AOA ve sheet ----------
      const aoa = [headerRow1, headerRow2, ...dataRows];
      const worksheet = XLSX.utils.aoa_to_sheet(aoa);

      // merges
      worksheet["!merges"] = worksheet["!merges"] || [];
      merges.forEach((m) => worksheet["!merges"].push(m));

      // column widths
      const cols = [];
      cols.push({ wch: 28 }); // Ürün
      clientList.forEach(() => {
        for (let i = 0; i < columnsPerClient; i++) cols.push({ wch: 18 });
      });
      worksheet["!cols"] = cols;

      // ---------- Stil: başlıklar bold ----------
      const encodeCell = XLSX.utils.encode_cell;
      const boldCell = (r, c) => {
        const addr = encodeCell({ r, c });
        const cell = worksheet[addr];
        if (!cell) return;
        cell.s = cell.s || {};
        cell.s.font = cell.s.font || {};
        cell.s.font.bold = true;
      };

      for (let c = 0; c < totalCols; c++) {
        boldCell(0, c);
        boldCell(1, c);
      }

      // ---------- Stil: % değişim kolonları (0.00) + renk ----------
      const firstDataRow = 2;
      const lastRow = aoa.length - 1;

      changeColIndexes.forEach((colIndex) => {
        for (let r = firstDataRow; r <= lastRow; r++) {
          const addr = encodeCell({ r, c: colIndex });
          const cell = worksheet[addr];
          if (!cell || cell.v == null || cell.v === "") continue;

          let val = cell.v;
          if (typeof val === "string") {
            const num = Number(val.replace(",", "."));
            if (!Number.isNaN(num)) val = num;
          }
          if (typeof val !== "number") continue;

          cell.v = val;
          cell.t = "n";
          cell.z = "0.00";

          // 0 ise hiç renklendirme yapma
          if (val === 0) continue;

          cell.s = cell.s || {};
          cell.s.font = cell.s.font || {};

          cell.s = cell.s || {};
          cell.s.font = cell.s.font || {};

          if (val < 0) {
            // kırmızı background
            cell.s.fill = {
              patternType: "solid",
              fgColor: { rgb: "FF0000" },
            };
            // okunurluk için beyaz yazı
            cell.s.font.color = { rgb: "FFFFFF" };
          } else if (val > 0) {
            // yeşil background
            cell.s.fill = {
              patternType: "solid",
              fgColor: { rgb: "008000" },
            };
            cell.s.font.color = { rgb: "FFFFFF" };
          }

        }
      });

      // ---------- Workbook ----------
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sipariş Raporu");

      const first = periods[0];
      const fileFrom = first?.from || "";
      const fileTo = first?.to || "";
      const fileName = `isletme_siparis_raporu_${fileFrom}_${fileTo}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    },

    async loadClients() {
      try {
        this.loading = true;
        const requestBody = {
          columnFilters: {},
          page: 1,
          perPage: 10000,
          sortField: "name",
          sortDirection: "asc",
        };
        const response = await ClientService.list(requestBody);
        this.clients =
            response.data.map((item) => ({
              ...item,
              name: item.shortName ? `${item.shortName} (${item.name})` : item.name,
            })) || [];
      } catch (error) {
        console.error("İşletme listesi alınırken hata oluştu:", error);
        this.$errorNotification &&
        this.$errorNotification(this, "İşletme listesi alınırken hata oluştu.");
      } finally {
        this.loading = false;
      }
    },

    async loadProducts() {
      try {
        this.loading = true;
        const requestParams = {
          columnFilters: {},
          sort: { field: "", type: "" },
          page: 1,
          perPage: 99999,
        };
        const response = await ProductService.getAdminList(requestParams);
        this.products = response.data.map((product) => ({
          id: product.id,
          name: product.name,
        }));
      } catch (err) {
        console.error("Ürün arama hatası:", err);
      } finally {
        this.loading = false;
      }
    },
  },

  async mounted() {
    await this.loadClients();
    await this.loadProducts();
  },
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
